import { useEffect, useState } from "react";
import { checkBackendReady } from "./readiness.js";

export type BackendReadinessStatus = "checking" | "ready" | "unavailable";

const REQUEST_TIMEOUT_MS = 8_000;
const UNAVAILABLE_RETRY_MS = 5_000;
const READY_RECHECK_MS = 60_000;

type ReadinessCheck = (signal?: AbortSignal) => Promise<void>;

/**
 * Monitors the API's database-backed /ready endpoint.
 *
 * A failed check retries quickly so a cold API/database can recover without a
 * page reload. Once ready, the slower recheck keeps the indicator honest
 * without continuously querying the database.
 */
export function useBackendReadiness(check: ReadinessCheck = checkBackendReady): BackendReadinessStatus {
  const [status, setStatus] = useState<BackendReadinessStatus>("checking");

  useEffect(() => {
    let cancelled = false;
    let nextCheckTimer: number | undefined;
    let inFlightController: AbortController | undefined;

    const runCheck = async () => {
      const controller = new AbortController();
      inFlightController = controller;
      const requestTimeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      let nextDelay = UNAVAILABLE_RETRY_MS;

      try {
        await check(controller.signal);
        if (cancelled) return;
        setStatus("ready");
        nextDelay = READY_RECHECK_MS;
      } catch {
        if (cancelled) return;
        setStatus("unavailable");
      } finally {
        window.clearTimeout(requestTimeout);
        if (inFlightController === controller) inFlightController = undefined;
        if (!cancelled) {
          nextCheckTimer = window.setTimeout(() => void runCheck(), nextDelay);
        }
      }
    };

    void runCheck();

    return () => {
      cancelled = true;
      if (nextCheckTimer !== undefined) window.clearTimeout(nextCheckTimer);
      inFlightController?.abort();
    };
  }, [check]);

  return status;
}
